import type { JSX } from 'react';
import { Link } from 'react-router-dom';

import type { Theme } from '@/types';
import { useSeo } from '@hooks/useSeo';
import { seoAsterion } from '@data/seo';
import { useContent } from '@data';
import { useT, useLang, pathFor } from '@/i18n';

import Navbar from '@components/nav/Navbar';
import './AsterionPage.css';

const ASTERION_URL = 'https://solutions-asterion.com';

interface AsterionPageProps {
  theme: Theme;
  onToggleTheme: () => void;
}

export default function AsterionPage({
  theme,
  onToggleTheme,
}: AsterionPageProps): JSX.Element {
  const { ASTERION_STORY } = useContent();
  const t = useT();
  const lang = useLang();

  useSeo(seoAsterion(lang));

  return (
    <>
      <Navbar theme={theme} onToggleTheme={onToggleTheme} />

      <main className="AsterionPage">
        <header className="AsterionPage__head">
          <img
            className="AsterionPage__logo AsterionPage__logo--light"
            src="/asterion/logo-asterion-solutions.svg"
            alt="Logo Asterion Solutions"
          />
          <img
            className="AsterionPage__logo AsterionPage__logo--dark"
            src="/asterion/logo-asterion-solutions-dark.svg"
            alt="Logo Asterion Solutions"
          />
          <p className="AsterionPage__label">{t.nav.founder}</p>
          <h1 className="AsterionPage__title">
            Pierre Séré et Asterion Solutions
          </h1>
          <p className="AsterionPage__intro">{t.founder.intro}</p>
        </header>

        {ASTERION_STORY.map((section) => (
          <section key={section.id} className="AsterionPage__section">
            <h2 className="AsterionPage__heading">{section.heading}</h2>
            {section.paragraphs.map((text) => (
              <p key={text.slice(0, 40)} className="AsterionPage__text">
                {text}
              </p>
            ))}
          </section>
        ))}

        <aside className="AsterionPage__cta">
          <p className="AsterionPage__ctaText">{t.founder.ctaText}</p>
          <a
            href={ASTERION_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="AsterionPage__ctaLink"
          >
            {t.founder.ctaLink}
          </a>
        </aside>

        <nav className="AsterionPage__nav" aria-label={t.founder.relatedAria}>
          <Link to={`${pathFor('/', lang)}#parcours`} className="AsterionPage__navLink">
            {t.founder.toTimeline}
          </Link>
          <Link to={pathFor('/articles', lang)} className="AsterionPage__navLink">
            {t.founder.toArticles}
          </Link>
        </nav>
      </main>

      <footer className="SiteFooter">
        <span className="SiteFooter__text">
          © 2026 Asterion Solutions inc. · pierresere.com
        </span>
        <Link to={pathFor('/', lang)} className="SiteFooter__text">
          {t.articles.indexBack}
        </Link>
      </footer>
    </>
  );
}
